import { useTranslation } from '../localization/useTranslation'
import { type CategoryOption, useCategoryOptions } from '../search/categories'
import { Glyph, IconArrowLeft, IconCamera, IconX } from '../ui/icons'
import { useMoneyFmt } from '../util/useMoney'
import { DetailDialog } from './DetailDialog'

/**
 * The add-by-category picker: section-grouped tiles (localized name + icon + flat reward value) the
 * customer picks when no catalogue match fits, or when the shop only offers categories. Reuses the
 * "your photo" strip from the photo results so the two screens feel like one flow; the photo the
 * customer took is shown at the top and passed on to the created potential.
 *
 * Presentational only — the owning page supplies the photo and the navigation/add callbacks.
 */
export function CategoryPicker({
  photoUrl,
  onBack,
  onReplace,
  onPick,
}: {
  photoUrl: string | null
  onBack: () => void
  onReplace: () => void
  onPick: (c: CategoryOption) => void
}) {
  const { t } = useTranslation()
  const fmt = useMoneyFmt()
  const { sections, loading, hasCategories } = useCategoryOptions()

  return (
    <>
      {/* Same "your photo" strip as the results view, so the two screens feel like one flow. The
          subtitle is swapped from "matched to our catalogue" to the category-selection note. */}
      <div className="uploaded">
        <span className="uthumb">
          {photoUrl ? <img src={photoUrl} alt={t('photo.yourPhoto')} /> : <IconCamera />}
        </span>
        <span className="utext">
          <b>{t('photo.yourPhoto')}</b>
          <small>{t('photo.categoryPickHint')}</small>
        </span>
        <button
          type="button"
          className="uploaded-x"
          onClick={onReplace}
          aria-label={t('photo.replace')}
        >
          <IconX />
        </button>
      </div>

      {/* Heading with a back arrow in front of it (tap to go back). */}
      <button type="button" className="cat-head" onClick={onBack}>
        <IconArrowLeft />
        <span className="photo-h">{t('photo.categoryPickTitle')}</span>
      </button>

      {loading && !hasCategories ? (
        <div className="analyzing">
          <span className="spin" aria-hidden />
        </div>
      ) : (
        sections.map((sec) => (
          <div className="cat-section" key={sec.section || '_'}>
            {sec.section && <div className="cat-section-h">{sec.section}</div>}
            <div className="grid cat-grid">
              {sec.categories.map((c) => (
                // The tile shows only the icon + name (no value) so it reads as "a type of item",
                // not a priced product; the flat value is revealed in the shared dialog on tap.
                <DetailDialog
                  key={c.id}
                  hero={c.iconUrl ? <img src={c.iconUrl} alt="" /> : <Glyph name="shirt" />}
                  heroContain
                  eyebrow={c.section || undefined}
                  title={c.name}
                  rewardLabel={t('photo.categoryValue')}
                  rewardValue={fmt(c.value)}
                  backLabel={t('photo.categoryBack')}
                  confirmLabel={t('photo.categoryConfirm')}
                  onConfirm={() => onPick(c)}
                  trigger={
                    <button type="button" className="card cat-card">
                      <div className="pic">
                        {c.iconUrl ? <img src={c.iconUrl} alt="" /> : <Glyph name="shirt" />}
                      </div>
                      <div className="cbody">
                        <div className="nm">{c.name}</div>
                      </div>
                    </button>
                  }
                />
              ))}
            </div>
          </div>
        ))
      )}
    </>
  )
}
